import axios from "axios";
import { useEffect, useState } from "react";
import { Clock3, Sparkles, X, Star, CheckCircle2, AlertTriangle } from "lucide-react";
import { API, money, statusLabel, AnalyticsTile, Modal, Empty } from "./shared";

// ============================================================
// MODAL: Rate delivery (buyer)
// ============================================================
export function RateDeliveryModal({ job, close, refresh }) {
  const [r, setR] = useState({ stars: 5, packaging_durability: 5, print_vibrancy: 5, unboxing: 5, communication: 5, feedback: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    await axios.post(`${API}/jobs/${job.id}/rate`, r);
    await refresh();
    setBusy(false);
    close();
  };
  const dims = [
    ["stars", "Overall rating"],
    ["packaging_durability", "Packaging durability"],
    ["print_vibrancy", "Print vibrancy"],
    ["unboxing", "Unboxing presentation"],
    ["communication", "Communication responsiveness"],
  ];
  return (
    <Modal title="Rate this delivery" close={close}>
      <p style={{ margin: "0 0 18px", fontSize: 13, color: "#475569" }}>
        Your rating feeds the supplier's Quality Score (CSAT pillar = 15%). Be candid — this signal helps buyers make sharper choices.
      </p>
      <form onSubmit={submit} className="rating-form">
        {dims.map(([k, label]) => (
          <div key={k} className="rating-row">
            <label>{label}</label>
            <div className="stars" data-testid={`rating-${k}`}>
              {[1, 2, 3, 4, 5].map(s => (
                <button
                  type="button"
                  key={s}
                  className={`star-btn ${r[k] >= s ? "on" : ""}`}
                  onClick={() => setR({ ...r, [k]: s })}
                  data-testid={`rating-${k}-${s}`}
                >★</button>
              ))}
              <b>{r[k]}</b>
            </div>
          </div>
        ))}
        <label className="rating-feedback">Feedback (optional)
          <textarea data-testid="rating-feedback" value={r.feedback} onChange={e => setR({ ...r, feedback: e.target.value })} placeholder="What went well, what could improve…"/>
        </label>
        <div className="form-actions">
          <button data-testid="rating-cancel" type="button" className="secondary-btn" onClick={close}>Cancel</button>
          <button data-testid="rating-submit" className="primary-btn" disabled={busy}>{busy ? "Saving…" : "Submit rating"} <Star size={15}/></button>
        </div>
      </form>
    </Modal>
  );
}

// ============================================================
// VIEW: SUPPLIER ANALYTICS (printer)
// ============================================================
export function SupplierAnalytics({ supplierId }) {
  const [data, setData] = useState(null);
  const [quality, setQuality] = useState(null);
  useEffect(() => {
    if (!supplierId) return;
    axios.get(`${API}/users/${supplierId}/analytics`).then(r => setData(r.data)).catch(() => {});
    axios.get(`${API}/users/${supplierId}/quality-score`).then(r => setQuality(r.data)).catch(() => {});
  }, [supplierId]);

  if (!data) return <section className="content"><Empty text="Loading bid analytics…"/></section>;

  const winRateColor = data.win_rate_pct >= 50 ? "ok" : data.win_rate_pct >= 25 ? "warn" : "bad";
  const scoreColor = data.avg_masked_score >= 85 ? "ok" : data.avg_masked_score >= 70 ? "warn" : "bad";
  return (
    <section className="content">
      <div className="section-heading">
        <div>
          <p className="eyebrow">BID INTELLIGENCE</p>
          <h2>Sharpen your next bid</h2>
          <p className="subhead">Track your win rate, weighted score benchmark, and how much you've been leaving on the table across lost sealed auctions.</p>
        </div>
      </div>

      {quality && <QualityScorePanel q={quality}/>}

      <div className="analytics-tiles">
        <AnalyticsTile
          testid="analytics-win-rate"
          label="Win rate"
          value={`${data.win_rate_pct}%`}
          note={`${data.wins} won · ${data.losses} lost · ${data.open} open`}
          tone={winRateColor}
        />
        <AnalyticsTile
          testid="analytics-avg-score"
          label="Avg masked score"
          value={data.avg_masked_score}
          note={`Weighted 50/30/20 across your ${data.total_bids} bids`}
          tone={scoreColor}
        />
        <AnalyticsTile
          testid="analytics-lost-delta"
          label="Lost-bid delta"
          value={data.avg_lost_delta > 0 ? `+${money(data.avg_lost_delta)}` : "—"}
          note={data.avg_lost_delta_pct > 0
            ? `On avg you priced ${data.avg_lost_delta_pct}% above the winner`
            : "No settled losses yet"}
          tone={data.avg_lost_delta_pct >= 15 ? "bad" : data.avg_lost_delta_pct >= 8 ? "warn" : "ok"}
        />
        <AnalyticsTile
          testid="analytics-closest-loss"
          label="Closest loss"
          value={data.closest_loss !== null && data.closest_loss !== undefined ? `+${money(data.closest_loss)}` : "—"}
          note={data.closest_loss !== null ? "Smallest gap between your price and the winner" : "No losses to compare"}
          tone="neutral"
        />
      </div>

      <div className="drawer-section-title" style={{ marginTop: 34 }}>
        <h3>Recent sealed bids</h3>
        <span>{data.recent.length} shown</span>
      </div>
      <div className="analytics-table">
        <div className="analytics-head">
          <span>JOB</span><span>MY PRICE</span><span>MY SCORE</span><span>OUTCOME</span><span>DELTA vs WINNER</span>
        </div>
        {data.recent.length === 0 ? <Empty text="No bids submitted yet."/> : data.recent.map((r, i) => (
          <div className="analytics-row" key={i} data-testid={`analytics-row-${i}`}>
            <div>
              <b>{r.job_title}</b>
              <span>{r.job_id} · {statusLabel[r.job_status] || r.job_status}</span>
            </div>
            <div><b>{money(r.my_price)}</b></div>
            <div><b>{r.my_masked_score}</b></div>
            <div>
              <span className={`outcome-pill outcome-${r.outcome}`}>
                {(r.outcome === "won" || r.outcome === "accepted") && <><CheckCircle2 size={12}/> Accepted</>}
                {(r.outcome === "lost" || r.outcome === "rejected") && <><X size={12}/> Rejected</>}
                {r.outcome === "submitted" && <><Clock3 size={12}/> Open</>}
              </span>
            </div>
            <div>
              {r.delta === null || r.delta === undefined
                ? <span className="muted">—</span>
                : r.delta > 0
                  ? <b className="delta-bad">+{money(r.delta)} <em>({r.delta_pct}%)</em></b>
                  : <b className="delta-ok">-{money(Math.abs(r.delta))}</b>}
            </div>
          </div>
        ))}
      </div>

      <div className="analytics-tip" data-testid="analytics-tip">
        <Sparkles size={16}/>
        <div>
          <b>Pricing coach</b>
          <span>
            {data.avg_lost_delta_pct >= 10
              ? `Trimming your quotes by ~${data.avg_lost_delta_pct}% on kraft-substrate jobs would have flipped your recent losses into wins.`
              : data.win_rate_pct >= 60
                ? "You're winning most bids — consider a modest price lift to protect margin on runs where you have no competition."
                : "Keep an eye on your masked score; boosting quality/performance ratings unlocks higher scores at the same price."}
          </span>
        </div>
      </div>
    </section>
  );
}

export function QualityScorePanel({ q }) {
  const tier = q.total_score >= 90 ? "elite" : q.total_score >= 75 ? "trusted" : q.total_score >= 60 ? "developing" : "risk";
  const tierLabel = { elite: "Elite", trusted: "Trusted", developing: "Developing", risk: "Needs attention" }[tier];
  return (
    <div className="quality-panel" data-testid="quality-score-panel">
      <div className="qp-head">
        <div>
          <p className="eyebrow">QUALITY SCORE · 5-PILLAR</p>
          <h3>Your operational rating</h3>
        </div>
        <div className="qp-total">
          <div className={`qp-tier tier-${tier}`} data-testid="quality-tier">{tierLabel}</div>
          <strong>{q.total_score}<em>/100</em></strong>
          <small>{q.stars_equivalent} ★ equivalent · {q.total_won_jobs} completed run(s)</small>
        </div>
      </div>
      <div className="qp-pillars">
        {q.pillars.map(p => (
          <div key={p.key} className="qp-pillar" data-testid={`pillar-${p.key}`}>
            <div className="qp-pillar-head">
              <span className="qp-key">{p.key}</span>
              <div>
                <b>{p.label}</b>
                <span className="qp-weight">Weight {p.weight}%</span>
              </div>
              <strong>{p.score}</strong>
            </div>
            <div className="qp-bar"><div style={{ width: `${p.score}%` }}/></div>
            <div className="qp-signals">
              {Object.entries(p.signals).map(([k, v]) => (
                <span key={k}>{k.replace(/_/g, " ")}: <b>{v ?? "—"}</b></span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SupplierAnalytics_afterSection() { return null; }

// ============================================================
// VIEW: PRINTER CAPABILITIES (5 Core Capability Matching Pillars)
// ============================================================
export function PrinterCapabilitiesPanel({ supplierId }) {
  const [caps, setCaps] = useState(null);
  const [ref, setRef] = useState({ press_types: [], post_press: [], product_capabilities: [], substrate_examples: [] });
  const [user, setUser] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!supplierId) return;
    Promise.all([
      axios.get(`${API}/users/${supplierId}/capabilities`),
      axios.get(`${API}/capabilities/reference`),
      axios.get(`${API}/users/${supplierId}`),
    ]).then(([c, r, u]) => {
      setCaps({
        press_types: c.data.press_types || [],
        substrates: c.data.substrates || [],
        post_press: c.data.post_press || [],
        product_capabilities: c.data.product_capabilities || [],
        min_quantity: c.data.min_quantity ?? 100,
        max_quantity: c.data.max_quantity ?? 100000,
        service_radius_km: c.data.service_radius_km ?? 400,
        home_city: c.data.home_city || "",
        home_state: c.data.home_state || "",
      });
      setRef(r.data);
      setUser(u.data);
    });
  }, [supplierId]);

  if (!caps) return <section className="content"><p className="subhead">Loading capabilities…</p></section>;

  const toggle = (key, val) => {
    const arr = caps[key] || [];
    const next = arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val];
    setCaps({ ...caps, [key]: next });
    setSaved(false);
  };

  const setField = (key, val) => { setCaps({ ...caps, [key]: val }); setSaved(false); };

  const addSubstrate = (val) => {
    const v = (val || "").trim();
    if (!v || caps.substrates.includes(v)) return;
    setCaps({ ...caps, substrates: [...caps.substrates, v] });
    setSaved(false);
  };
  const removeSubstrate = (v) => {
    setCaps({ ...caps, substrates: caps.substrates.filter(x => x !== v) });
    setSaved(false);
  };

  const save = async () => {
    await axios.patch(`${API}/users/${supplierId}/capabilities`, {
      ...caps,
      min_quantity: Number(caps.min_quantity),
      max_quantity: Number(caps.max_quantity),
      service_radius_km: Number(caps.service_radius_km),
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <section className="content" data-testid="printer-capabilities-panel">
      <div className="section-heading">
        <div>
          <p className="eyebrow">5 CORE CAPABILITY MATCHING PILLARS</p>
          <h2>Your production profile</h2>
          <p className="subhead">Buyers' RFQs are routed to you only when their spec matches at least 60% across the five pillars below. Keep this accurate to avoid low-fit auctions.</p>
        </div>
        <button data-testid="caps-save-button" className="primary-btn" onClick={save}>
          {saved ? "Saved ✓" : "Save capabilities"}
        </button>
      </div>

      {user && (
        <div className={`verify-banner ${user.is_verified ? "ok" : "pending"}`} data-testid="verification-banner">
          {user.is_verified ? <CheckCircle2 size={17}/> : <AlertTriangle size={17}/>}
          <div>
            <b>{user.is_verified ? "Verified supplier" : `Verification stage: ${user.verification_stage || "Pending Documents"}`}</b>
            <span>{user.is_verified
              ? "You're eligible to receive sealed auctions and submit bids."
              : "You will not receive auctions or be able to bid until admin marks you Verified."}</span>
          </div>
        </div>
      )}

      <div className="caps-grid">
        <div className="caps-pillar">
          <div className="pillar-header"><b>1 · Machine & Press Type</b><span>Select every press you own</span></div>
          <div className="chip-row">
            {ref.press_types.map(pt => (
              <button
                key={pt}
                type="button"
                data-testid={`cap-press-${pt}`}
                className={`chip ${caps.press_types.includes(pt) ? "on" : ""}`}
                onClick={() => toggle("press_types", pt)}
              >{pt}</button>
            ))}
          </div>
        </div>

        <div className="caps-pillar">
          <div className="pillar-header"><b>2 · Substrate</b><span>Materials you can run reliably</span></div>
          <div className="chip-row">
            {caps.substrates.map(s => (
              <span key={s} className="chip on removable" data-testid={`cap-sub-tag-${s}`}>
                {s} <button type="button" onClick={() => removeSubstrate(s)} aria-label={`Remove ${s}`}>×</button>
              </span>
            ))}
          </div>
          <input
            data-testid="cap-substrate-input"
            className="cap-add-input"
            placeholder="Add substrate (e.g. Kraft 120gsm) and press Enter"
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addSubstrate(e.currentTarget.value); e.currentTarget.value = ""; } }}
          />
          <div className="chip-row muted">
            <small style={{ marginRight: 8 }}>Suggestions:</small>
            {ref.substrate_examples.map(s => (
              <button key={s} type="button" className="chip suggest" onClick={() => addSubstrate(s)}>+ {s}</button>
            ))}
          </div>
        </div>

        <div className="caps-pillar">
          <div className="pillar-header"><b>3 · Post-Press / Finishing</b><span>Every finish your bindery can produce</span></div>
          <div className="chip-row">
            {ref.post_press.map(pp => (
              <button
                key={pp}
                type="button"
                data-testid={`cap-post-${pp}`}
                className={`chip ${caps.post_press.includes(pp) ? "on" : ""}`}
                onClick={() => toggle("post_press", pp)}
              >{pp}</button>
            ))}
          </div>
        </div>

        <div className="caps-pillar">
          <div className="pillar-header"><b>4 · Product Capabilities</b><span>Hard gate — you'll only be matched to jobs whose product type you produce</span></div>
          <div className="chip-row">
            {(ref.product_capabilities || []).map(pc => (
              <button
                key={pc}
                type="button"
                data-testid={`cap-product-${pc}`}
                className={`chip ${caps.product_capabilities.includes(pc) ? "on" : ""}`}
                onClick={() => toggle("product_capabilities", pc)}
              >{pc}</button>
            ))}
          </div>
        </div>

        <div className="caps-pillar">
          <div className="pillar-header"><b>5 · Quantity / Run-Length</b><span>Feasible minimum and maximum per run</span></div>
          <div className="cap-quantity-row">
            <label>Minimum
              <input data-testid="cap-min-qty" type="number" value={caps.min_quantity} onChange={e => setField("min_quantity", e.target.value)}/>
            </label>
            <label>Maximum
              <input data-testid="cap-max-qty" type="number" value={caps.max_quantity} onChange={e => setField("max_quantity", e.target.value)}/>
            </label>
          </div>
        </div>

        <div className="caps-pillar">
          <div className="pillar-header"><b>6 · Geographic Radius</b><span>Where you can economically ship</span></div>
          <div className="cap-geo-row">
            <label>Home city
              <input data-testid="cap-home-city" value={caps.home_city} onChange={e => setField("home_city", e.target.value)}/>
            </label>
            <label>State
              <input data-testid="cap-home-state" value={caps.home_state} onChange={e => setField("home_state", e.target.value)}/>
            </label>
            <label>Service radius (km)
              <input data-testid="cap-radius" type="number" value={caps.service_radius_km} onChange={e => setField("service_radius_km", e.target.value)}/>
            </label>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// 5-PILLAR MATCH SCORE BADGE (used in drawer)
// ============================================================
export function CapabilityMatchPanel({ jobId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/jobs/${jobId}/matching-suppliers`).then(r => setData(r.data)).catch(() => setData({ results: [] }));
  }, [jobId]);
  if (!data) return null;
  if (!data.results || data.results.length === 0) return null;
  return (
    <div className="cap-match-panel" data-testid="capability-match-panel">
      <div className="drawer-section-title">
        <h3>5-Pillar capability match</h3>
        <span>Threshold {data.threshold}% · scored across press, substrate, finishing, quantity & radius</span>
      </div>
      {data.results.map(r => (
        <div key={r.supplier_id} className={`match-row ${r.eligible ? "ok" : "block"}`} data-testid={`match-row-${r.supplier_id}`}>
          <div className="match-head">
            <b>
              {r.name}
              {r.is_verified ? (
                <span className="verify-pill ok" data-testid={`verify-badge-${r.supplier_id}`}><CheckCircle2 size={10}/> Verified</span>
              ) : (
                <span className="verify-pill pending" data-testid={`verify-badge-${r.supplier_id}`}><AlertTriangle size={10}/> {r.verification_stage}</span>
              )}
            </b>
            <span className={`match-score ${r.eligible ? "ok" : "block"}`}>{r.match.total_score}%</span>
          </div>
          {r.match.product_gate && !r.match.product_gate.passed && (
            <div className="gate-warning" data-testid={`gate-warn-${r.supplier_id}`}>
              <AlertTriangle size={12}/> {r.match.product_gate.reason}
            </div>
          )}
          <div className="match-pillars">
            {Object.entries(r.match.pillars).map(([k, v]) => (
              <span key={k} className={`pillar-chip ${v.score >= v.max ? "full" : v.score > 0 ? "partial" : "none"}`} title={v.reason}>
                {k.replace(/_/g, " ")}: {v.score}/{v.max}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
